import type { LockedPeriodImportCandidateDto } from '@signalvote/shared';
import { formatAmount, formatDate } from '../utils';

/**
 * The pre-commit heads-up for import rows dated inside an already-filed (locked)
 * reporting period. Shared by BOTH import preview surfaces — the processor /
 * compiled detector preview (`FileTypeDetectorPanel`) and the bank column-mapping
 * preview (`BankColumnMappingPanel`) — so the two paths render one identical
 * story.
 *
 * Product intent (approved UX): inform, never block. These rows are SKIPPED by
 * default (every checkbox starts unchecked) so a re-uploaded year-to-date file
 * can't double-report what's already on file. The treasurer may include any — or
 * all, via "Include all" — after which the commit carries their `rowIndex`es in
 * `includeLockedRowIndices`. Tone is calm soft-amber (never red): including a row
 * doesn't touch what's already filed; it just brings the row in so the treasurer
 * can amend the filed report in their state's portal.
 *
 * Purely presentational: selection state lives in the parent surface (per file /
 * per preview), so a live re-preview can reconcile it against the new candidate
 * set. Renders nothing when there are no candidates.
 */
interface LockedPeriodSkipPanelProps {
  candidates: LockedPeriodImportCandidateDto[];
  /** The `rowIndex`es the treasurer has chosen to include (checked). */
  selected: number[];
  /** Toggle one row's inclusion by its stable `rowIndex`. */
  onToggle: (rowIndex: number) => void;
  /** Include-all when any are held; skip-all when every row is already included. */
  onToggleAll: () => void;
}

export function LockedPeriodSkipPanel({
  candidates,
  selected,
  onToggle,
  onToggleAll,
}: LockedPeriodSkipPanelProps) {
  if (candidates.length === 0) return null;

  const selectedSet = new Set(selected);
  const allIncluded = candidates.every((c) => selectedSet.has(c.rowIndex));
  const rowWord = candidates.length === 1 ? 'row is' : 'rows are';

  return (
    // Soft-amber, never red: a calm "so you don't double-report" heads-up, not an
    // error. Counts + copy wrap (no fixed widths / no truncate) so they stay
    // legible at font-scale 1.3 / 200% zoom.
    <section
      aria-label="Rows dated in an already-filed period"
      className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm space-y-3 text-amber-900"
    >
      <p>
        {candidates.length} {rowWord} dated in periods you&rsquo;ve already filed
        &mdash; skipped by default so you don&rsquo;t double-report:
      </p>

      <ul className="space-y-1.5">
        {candidates.map((c) => {
          const checked = selectedSet.has(c.rowIndex);
          // Label the checkbox with the row's identity so a screen reader (and
          // the tests) can address each row by its donor/payee name.
          const rowLabel = `Include ${c.label} — ${formatDate(c.date)} ${formatAmount(c.amount)}`;
          return (
            <li key={c.rowIndex}>
              <label className="flex flex-wrap items-center gap-x-4 gap-y-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(c.rowIndex)}
                  aria-label={rowLabel}
                  className="h-4 w-4 shrink-0 accent-amber-600"
                />
                <span className="text-amber-900/90">{formatDate(c.date)}</span>
                <span className="tabular-nums text-amber-900/90">{formatAmount(c.amount)}</span>
                <span className="font-medium">{c.label}</span>
              </label>
            </li>
          );
        })}
      </ul>

      <div>
        <button
          type="button"
          onClick={onToggleAll}
          className="min-h-11 rounded-md border border-amber-300 bg-white/60 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
        >
          {allIncluded ? 'Skip all' : 'Include all'}
        </button>
      </div>

      <p className="text-amber-800/90">
        Including one won&rsquo;t change what&rsquo;s already filed &mdash;
        you&rsquo;d amend that report in your state&rsquo;s filing portal.
      </p>
    </section>
  );
}

/**
 * The selection-reflecting suffix for a preview's primary import button. Matches
 * the approved mockup: "(+2 included)" once the treasurer opts locked rows in,
 * "(+0 held)" when they're all still held back. Callers append it to their verb
 * label only when there ARE locked candidates.
 */
export function lockedIncludeHint(includedCount: number): string {
  return includedCount > 0 ? ` (+${includedCount} included)` : ' (+0 held)';
}
